import { useWindowSize } from "hooks/useWindowSize";
import {
  INVOICE_PANE_TAB,
  InvoicePaneTab,
  MobileInvoicePaneTabs,
} from "pages/Books/Invoicing/MobileInvoicePaneTabs";
import classNames from "classnames";
import { DashboardLayout } from "components/DashboardLayout";
import { Divider } from "components/design/Divider";
import { InvoiceHeader } from "components/design/InvoiceHeader";
import Loader from "components/DesignSystem/Loader/loader";
import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { FormikListener } from "components/FormikListener/FormikListener";
import { PreviewBody } from "components/PreviewModal";
import { BillUpdateOptionsModalContent } from "./BillUpdateOptionsModalContent";
import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import {
  BILL_STATUS,
  BILL_STATUS_LABEL,
  BILLS_SOURCES,
  TAG_TYPE,
} from "constants/vendorBills";
import { Form, Formik } from "formik";
import { vendorBillSchema } from "formValidations/vendorBillSchema";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePageTitle } from "hooks/usePageTitle";
import { useToast } from "hooks/useToast";
import { useModal } from "hooks/useModal";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router-dom";
import { useQuery } from "hooks/useQuery";
import DoubleGreenTick from "static/images/DoubleGreenTick.svg";
import UploadFile from "static/images/UploadFile.svg";
import { useGetPreviewUrlQuery } from "store/apis/previewUrl";
import {
  UpdatePayload,
  useGetBillOrRequestQuery,
  useUpdateBillOrRequestMutation,
} from "store/apis/vendorBills";
import { BackendError } from "types/utils/error";
import {
  BillDetails,
  ItemDetails,
  PaymentDetails,
  TaxAndDiscount,
  VendorAddress,
  VendorDetails,
} from "./BillConsole";
import { useGetExchangeRateQuery } from "store/apis/invoices";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { diffValues } from "utils/diffValues";
import { formatDate } from "utils/formatDate";
import { formatNumber } from "utils/formatNumber";
import { USD } from "constants/currency";
import { useRef, useState } from "react";
import FocusBlock from "components/DesignSystem/FocusBlock/FocusBlock";
import { BillInfo } from "./BillInfo";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ActionItems } from "./BillActionItems";
import { OptionDropdown } from "components/icons/optionDropdown";
import { useBillActions } from "./useBillActions";
import { ColorInfo } from "components/icons/ColorInfo";
import { LinkInvoiceToTransactionModal } from "components/InvoiceAction/LinkInvoiceToTransactionModal";
import { UnlinkTransactionModal } from "components/UnlinkTransactionModal/UnlinkTransactionModal";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedUi } from "components/LockedUi/LockedUi";

type AddBillProps = {
  close: () => void;
};

const TransactionUnlinkButton = ({
  transactionId,
  billId,
}: {
  transactionId: string;
  billId: string;
}) => {
  const unlinkModal = useModal();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.EXPENSE
  );

  if (!hasWritePermission) {
    return null;
  }

  return (
    <>
      <button
        className="all:unset t-text-purple-40"
        onClick={(e) => {
          e.stopPropagation();
          unlinkModal.open();
        }}
      >
        Unlink
      </button>

      {unlinkModal.isOpen && (
        <UnlinkTransactionModal
          isOpen={unlinkModal.isOpen}
          onClose={unlinkModal.close}
          invoiceId={billId}
          transactionId={transactionId}
          financialRecordType="BILL"
        />
      )}
    </>
  );
};

export const AddBillPage = ({ close }: AddBillProps) => {
  const { billId } = useParams<{ billId: string }>();
  const query = useQuery();
  const [overlayActiveItem, setOverlayActiveItem] = useState<string | null>(
    null
  );
  const [tempBillFile, setTempBillFile] = useState<File[]>([]);
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { link } = useConstructInternalLink();

  const prevBillValuesRef = useRef<Partial<UpdatePayload> | null>(null);

  const billUpdateOptionModal = useModal();
  const paymentModal = useModal();
  const [paymentBillId, setPaymentBillId] = useState<string | undefined>();

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    billUpdateOptionModal.open();
    setTempBillFile(acceptedFiles);
  };

  const onDropRejected = () => {
    alertToast({ message: "Please upload a PDF file exclusively." });
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject, open } =
    useDropzone({
      noClick: true,
      noKeyboard: true,
      onDrop,
      maxSize: 25000000,
      accept: {
        "application/pdf": [".pdf"],
      },
      onDropRejected,
      multiple: false,
    });

  const {
    data: bill,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetBillOrRequestQuery(
    { groupId, entityId, billId },
    {
      skip: !billId || !entityId || !groupId,
    }
  );

  const { data: exchangeRate } = useGetExchangeRateQuery(
    {
      base_currency: "USD",
      target_currency: bill?.currency!,
      date: bill?.bill_date!,
    },
    {
      skip: !bill || !bill?.bill_date,
    }
  );

  usePageTitle(bill?.bill_number ? `Add ${bill?.bill_number}` : "Add Bill");

  const fileurl = useGetPreviewUrlQuery(
    {
      fileId: bill?.bill_document?.uuid,
      groupId,
    },
    {
      skip: !bill?.bill_document?.uuid || !groupId,
    }
  );

  const [
    updateBillOrRequest,
    { isLoading: isUpdating, isSuccess: isUpdated, isError: isUpdateError },
  ] = useUpdateBillOrRequestMutation();

  if (bill && prevBillValuesRef.current === null) {
    prevBillValuesRef.current = bill;
  }

  const onChange = async (value: Partial<UpdatePayload>) => {
    if (prevBillValuesRef.current === null) {
      return;
    }

    const payload = diffValues(prevBillValuesRef.current, value);

    if (Object.keys(payload).length === 0) {
      return;
    }

    if (Object.keys(payload).length === 1 && "invoice_items" in payload) {
      prevBillValuesRef.current = value;
      return;
    }

    try {
      await updateBillOrRequest({
        billId,
        entityId,
        groupId,
        payload: {
          ...payload,
          mark_as_active: true,
        },
      }).unwrap();
      prevBillValuesRef.current = value;
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError)?.data?.error?.message,
        },
        error as Error
      );
    }
  };

  const [markActive, { isLoading: isMarkingActive }] =
    useUpdateBillOrRequestMutation();

  const onAddBill = async () => {
    try {
      await markActive({
        billId,
        entityId,
        groupId,
        payload: {
          bill_payment_status: BILL_STATUS.DUE,
        },
      }).unwrap();
      close();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError)?.data?.error?.message,
        },
        error as Error
      );
    }
  };

  const handleRecordPayment = (billId: string) => {
    setPaymentBillId(billId);
    paymentModal.open();
  };

  const closePaymentModal = () => {
    setPaymentBillId(undefined);
    paymentModal.close();
  };

  const {
    onDelete,
    onDownload,
    onSendRequest,
    onStatusChange,
    onDuplicate,
    isDownloading,
    isDeleting,
    isUpdatingStatus,
    isDuplicating,
  } = useBillActions({
    bill,
    onRecordPayment: handleRecordPayment,
    isFromBillDetails: true,
  });

  const isActionLoading =
    isDownloading || isDeleting || isUpdatingStatus || isDuplicating;

  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.EXPENSE
  );
  const isBillForwarded =
    bill?.source === BILLS_SOURCES.FORWARDED_VIA_EMAIL.value;

  const isDraft = bill?.bill_payment_status === BILL_STATUS.DRAFT;
  const { isMobile } = useWindowSize();
  const [activePane, setActivePane] = useState<InvoicePaneTab>(
    INVOICE_PANE_TAB.EDIT
  );
  const isEditPaneHidden = activePane !== INVOICE_PANE_TAB.EDIT;
  const isPreviewPaneHidden = activePane !== INVOICE_PANE_TAB.PREVIEW;
  const [isEditFlow, setIsEditFlow] = useState(() =>
    Boolean(query.get("editflow"))
  );
  const onEdit =
    !isDraft && hasWritePermission ? () => setIsEditFlow(true) : undefined;

  return (
    <Modal.Root open={true} modal={false}>
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={false}
        isError={isError}
        customLoader={
          <Modal.Content size="fullscreen">
            <Loader />
          </Modal.Content>
        }
      >
        <Async.ErrorHandler>
          <Modal.Content size="fullscreen">
            <Modal.Body className="t-min-h-80 t-w-full t-h-full t-flex t-justify-center t-flex-col t-items-center t-gap-2 t-text-subtitle-sm t-text-red">
              <>
                {(error as BackendError)?.data?.error?.message ||
                  "Something went wrong, Please try again later."}
              </>
              <Button onClick={close} size="small">
                Go back
              </Button>
            </Modal.Body>
          </Modal.Content>
        </Async.ErrorHandler>
        <Async.Empty>
          <></>
        </Async.Empty>
        <Async.Success>
          {bill && (
            <Modal.Content
              size="fullscreen"
              className="t-grid t-grid-cols-2 sm:t-grid-cols-1"
            >
              <Formik<UpdatePayload>
                initialValues={bill}
                enableReinitialize={isUpdateError}
                onSubmit={onAddBill}
                validateOnChange
                validationSchema={vendorBillSchema}
              >
                {({ values, isValid, submitForm }) => {
                  const savedStatus = (
                    <Async.Root
                      isLoading={isUpdating}
                      customLoader={
                        <div className="t-flex t-items-center">
                          <Loader customType="secondary" />
                        </div>
                      }
                      isEmpty={false}
                      isSuccess={isUpdated}
                    >
                      <Async.Empty>
                        <></>
                      </Async.Empty>
                      <Async.Success>
                        <span className="t-flex t-items-center t-gap-2 t-transition-all t-text-text-30 t-text-body-sm">
                          <img src={DoubleGreenTick} alt="Saved" />
                          Changes saved
                        </span>
                      </Async.Success>
                    </Async.Root>
                  );

                  const mobileTabs = (
                    <MobileInvoicePaneTabs
                      value={activePane}
                      onChange={setActivePane}
                      previewLabel="Attachment"
                      right={
                        <span className="t-text-body-sm">{savedStatus}</span>
                      }
                    />
                  );

                  const journalLink = (
                    <>
                      {bill.ledger_entry?.uuid ? (
                        <ConditionalLink
                          target="_blank"
                          to={link("/books/journal-entries", {
                            moreQuery: {
                              journal_entry_id: bill.ledger_entry.uuid,
                            },
                          })}
                        >
                          <Button customType="link" size="small" type="button">
                            View journal entry
                          </Button>
                        </ConditionalLink>
                      ) : (
                        <span />
                      )}
                    </>
                  );

                  const actionRow = (
                    <div className="t-flex t-gap-2 t-items-center">
                      <span className="sm:t-hidden">{savedStatus}</span>
                      {(isDraft || isEditFlow) && (
                        <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
                          <Button
                            size="small"
                            type="submit"
                            customType="primary"
                            onClick={submitForm}
                            isLoading={isMarkingActive}
                            disabled={
                              isMarkingActive ||
                              !isValid ||
                              !bill.entity_merchant_data?.uuid ||
                              !bill.bill_date ||
                              bill.invoice_items.length <= 0
                            }
                          >
                            Mark as active
                          </Button>
                        </LockedUi>
                      )}
                      <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
                        <Dropdown.Root>
                          <Dropdown.Trigger asChild disabled={isActionLoading}>
                            <Button
                              onClick={(e) => e.stopPropagation()}
                              customType="icon"
                              size="small"
                              isLoading={isActionLoading}
                              disabled={isActionLoading}
                            >
                              <span className="t-text-text-30">
                                <OptionDropdown />
                              </span>
                            </Button>
                          </Dropdown.Trigger>
                          <Dropdown.Portal>
                            <Dropdown.Content
                              align="end"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ActionItems
                                bill={bill}
                                onDelete={onDelete}
                                onDownload={onDownload}
                                onSendRequest={onSendRequest}
                                onStatusChange={onStatusChange}
                                onDuplicate={onDuplicate}
                                onEdit={
                                  !isDraft
                                    ? () => setIsEditFlow(true)
                                    : undefined
                                }
                                hideMarkAsActive
                              />
                            </Dropdown.Content>
                          </Dropdown.Portal>
                        </Dropdown.Root>
                      </LockedUi>
                    </div>
                  );

                  return (
                    <>
                      <Form
                        className={classNames("t-overflow-auto", {
                          "sm:t-hidden": isEditPaneHidden,
                          "sm:t-pb-24": !isEditPaneHidden,
                        })}
                      >
                        {(isDraft || isEditFlow) && (
                          <LockedUi feature={WRITE_PERMISSION_KEYS.EXPENSE}>
                            <FormikListener
                              debounce
                              values={values}
                              callback={onChange}
                            />
                          </LockedUi>
                        )}
                        <DashboardLayout
                          className="t-pt-6 t-relative"
                          header={
                            <div className="sm:t-pb-4">
                              <InvoiceHeader
                                onBackClick={close}
                                status={
                                  <Tag
                                    rounded
                                    icon={false}
                                    tagType={
                                      TAG_TYPE[bill?.bill_payment_status]
                                    }
                                  >
                                    {
                                      BILL_STATUS_LABEL[
                                        bill?.bill_payment_status
                                      ]
                                    }
                                  </Tag>
                                }
                                title={bill?.bill_number}
                                right={
                                  <>
                                    {bill.associated_txns &&
                                      bill.associated_txns.length > 0 && (
                                        <ToolTip
                                          text={
                                            <div
                                              className="t-min-w-64 t-text-left t-flex t-flex-col t-gap-0.5"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              {bill.associated_txns.map(
                                                (txn) => (
                                                  <div
                                                    className="t-flex t-justify-between"
                                                    key={txn.uuid}
                                                  >
                                                    <div className="t-flex t-justify-between t-items-center">
                                                      <div>
                                                        {formatDate(txn.date)}
                                                      </div>
                                                    </div>
                                                    <ConditionalLink
                                                      target="_blank"
                                                      to={`/books/transactions/?entity=${entityId}&company=${groupId}&transaction_id=${txn.uuid}`}
                                                    >
                                                      <Button
                                                        customType="link"
                                                        size="small"
                                                        type="button"
                                                      >
                                                        <span className="t-text-white t-underline t-ml-2">
                                                          <AmountSuperScript
                                                            amount={Number(
                                                              txn.amount
                                                            )}
                                                          />
                                                        </span>
                                                      </Button>
                                                    </ConditionalLink>
                                                    <div className="t-ml-auto">
                                                      <TransactionUnlinkButton
                                                        transactionId={txn.uuid}
                                                        billId={billId}
                                                      />
                                                    </div>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          }
                                        >
                                          <span>
                                            <ConditionalLink
                                              target="_blank"
                                              to={`/books/transactions/?entity=${entityId}&company=${groupId}&transaction_id=${bill.associated_txns?.[0].uuid}`}
                                            >
                                              <Button
                                                customType="link"
                                                size="small"
                                                type="button"
                                              >
                                                View linked
                                                {bill.associated_txns.length ===
                                                1
                                                  ? " transaction"
                                                  : " transactions"}{" "}
                                                ({bill.associated_txns.length})
                                              </Button>
                                            </ConditionalLink>
                                          </span>
                                        </ToolTip>
                                      )}
                                  </>
                                }
                              />
                              {isMobile && mobileTabs}
                            </div>
                          }
                        >
                          {isDraft || (isEditFlow && hasWritePermission) ? (
                            <FocusBlock.Root
                              value={overlayActiveItem}
                              onValueChange={setOverlayActiveItem}
                            >
                              <div>
                                {" "}
                                {isBillForwarded &&
                                  bill.bill_payment_status ===
                                    BILL_STATUS.DRAFT && (
                                    <div className="t-rounded-lg t-bg-purple-0 t-border t-border-solid t-border-purple-30 t-p-2.5 t-flex t-items-center t-gap-2 t-text-purple t-mb-4">
                                      <ColorInfo />
                                      <div className="t-text-body-sm t-text-text-30">
                                        You are editing a bill forwarded via
                                        email
                                      </div>
                                    </div>
                                  )}
                              </div>
                              <div className="t-flex t-flex-col t-gap-12 t-pb-10">
                                <VendorDetails
                                  bill={{
                                    ...values,
                                    customer_ai_status: bill.customer_ai_status,
                                  }}
                                  onChange={onChange}
                                />
                                <VendorAddress
                                  bill={values}
                                  onChange={onChange}
                                />
                                <BillDetails bill={values} />
                                <ItemDetails
                                  bill={bill}
                                  groupId={groupId}
                                  entityId={entityId}
                                  setOverlayActiveItem={setOverlayActiveItem}
                                  isBillLineItem={true}
                                  showTotals={true}
                                />
                                <TaxAndDiscount bill={values} />

                                {bill && bill.invoice_items.length > 0 && (
                                  <div className="">
                                    <Divider type="dashed" color="dark-grey" />
                                    <div className="t-flex t-gap-3 t-flex-col t-mt-6">
                                      <div className="t-flex t-justify-between t-items-center">
                                        <span className="t-text-body-sm">
                                          Total
                                        </span>
                                        <span className="t-text-subtext">
                                          <AmountSuperScript
                                            amount={Number(bill.due_balance)}
                                            currencyCode={bill.currency}
                                          />{" "}
                                        </span>
                                      </div>

                                      {bill.currency !== USD && (
                                        <>
                                          <div className="t-flex t-justify-between t-items-center">
                                            <span className="t-text-body-sm t-text-text-30">
                                              USD Equivalent
                                            </span>
                                            <span className="t-text-subtext">
                                              {exchangeRate ? (
                                                <AmountSuperScript
                                                  amount={
                                                    Number(bill.due_balance) /
                                                    Number(
                                                      exchangeRate?.exchange_rate
                                                    )
                                                  }
                                                />
                                              ) : (
                                                "..."
                                              )}
                                            </span>
                                          </div>

                                          <Banner
                                            type="info"
                                            description={`Exchange Rate (as of  ${formatDate(
                                              bill.bill_date
                                            )}): 1.00 USD = ${
                                              exchangeRate
                                                ? formatNumber(
                                                    Number(
                                                      exchangeRate?.exchange_rate
                                                    )
                                                  )
                                                : "..."
                                            } ${bill.currency} `}
                                          />
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}

                                <PaymentDetails bill={values} paymentlink />
                              </div>
                            </FocusBlock.Root>
                          ) : (
                            <BillInfo bill={bill} />
                          )}
                        </DashboardLayout>
                      </Form>
                      <DashboardLayout
                        className={classNames(
                          "t-pt-6 t-bg-surface-lighter-grey sm:t-pb-24 sm:t-overflow-auto",
                          { "sm:t-hidden": isPreviewPaneHidden }
                        )}
                        contentProps={{
                          className:
                            "t-mb-6 sm:t-overflow-visible sm:t-flex-none",
                        }}
                        header={
                          isMobile ? (
                            <div className="t-bg-surface t-pb-4">
                              {mobileTabs}
                            </div>
                          ) : (
                            <div className="t-flex t-justify-between !t-bg-surface-lighter-grey t-items-center">
                              {journalLink}
                              {actionRow}
                            </div>
                          )
                        }
                      >
                        <div className="t-flex t-flex-col t-gap-4 t-h-full sm:t-h-auto">
                          <h4 className="t-text-subtitle t-text-text-100 t-m-0">
                            Bill Attachment
                          </h4>
                          <Divider />
                          <Async.Root
                            isLoading={fileurl.isLoading}
                            isSuccess={fileurl.isSuccess || isSuccess}
                            isEmpty={!fileurl.data || !bill.bill_document}
                          >
                            <Async.Empty>
                              <button
                                type="button"
                                className={classNames(
                                  "all:unset t-flex t-flex-col t-items-center t-justify-center t-bg-white t-rounded-md t-shadow-light-30 t-h-full t-border-dashed t-border-purple-30 t-border t-text-center t-text-text-30 t-text-body-sm sm:t-min-h-screen",
                                  {
                                    "!t-border-green": isDragAccept,
                                    "!t-border-red-50": isDragReject,
                                  }
                                )}
                                {...getRootProps()}
                                onClick={open}
                              >
                                <input {...getInputProps()} id="csv" />
                                <img src={UploadFile} alt="UploadFile" />
                                <>
                                  Click or drag and drop to upload the in .pdf
                                  format
                                </>
                              </button>
                            </Async.Empty>
                            <Async.Success>
                              {bill.bill_document &&
                                fileurl.data?.preview_url && (
                                  <div className="[&_img]:!t-top-0 t-h-full sm:t-h-auto">
                                    <PreviewBody
                                      file={bill.bill_document}
                                      preview_url={fileurl.data?.preview_url}
                                      fullWidth
                                    />
                                  </div>
                                )}
                            </Async.Success>
                          </Async.Root>
                        </div>
                      </DashboardLayout>

                      {isMobile && (
                        <div className="t-fixed t-bottom-0 t-left-0 t-right-0 t-z-header t-flex t-items-center t-justify-between t-gap-2 t-bg-surface t-px-3 t-py-3 t-shadow-light-100">
                          {journalLink}
                          {actionRow}
                        </div>
                      )}

                      <BillUpdateOptionsModalContent
                        isOpen={billUpdateOptionModal.isOpen}
                        close={billUpdateOptionModal.close}
                        tempBillFile={tempBillFile}
                        billId={billId}
                        onModalClose={() => setTempBillFile([])}
                      />
                    </>
                  );
                }}
              </Formik>
            </Modal.Content>
          )}
        </Async.Success>
      </Async.Root>
      {paymentBillId && (
        <LinkInvoiceToTransactionModal
          type="BILL"
          invoiceId={paymentBillId}
          isOpen={paymentModal.isOpen}
          close={closePaymentModal}
        />
      )}
    </Modal.Root>
  );
};
